import React from "react";
import { X, XIcon, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Paperclip, Mail, Upload, Download, Eye, Trash2, File, FileText, Image, MessageSquare, Copy, Package, CreditCard, RefreshCw, Printer } from "lucide-react";
import { toast } from "sonner";

export function AttachmentsModal({
  attachmentModal, closeAttachmentModal, handleFileUpload, isUploading,
  attachments, previewAttachment, downloadAttachment, handleDeleteAttachment,
  handleTogglePrintWithSheet,
}) {
  React.useEffect(() => {
    if (!attachmentModal.open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") closeAttachmentModal(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [attachmentModal.open, closeAttachmentModal]);
  if (!attachmentModal.open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeAttachmentModal} data-testid="attachments-modal-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="attachments-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-3"><Paperclip className="w-5 h-5 text-purple-600" /><h3 className="text-lg font-semibold text-zinc-900">Pièces jointes - {attachmentModal.orderName}</h3></div>
          <button onClick={closeAttachmentModal} className="p-1 hover:bg-zinc-200 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto max-h-[60vh]">
          <div className="mb-4">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-zinc-300 rounded-lg cursor-pointer hover:bg-zinc-50 transition-colors">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-8 h-8 mb-2 text-zinc-400" />
                <p className="text-sm text-zinc-500">{isUploading ? "Upload en cours..." : "Cliquez ou glissez-déposez vos fichiers"}</p>
                <p className="text-xs text-zinc-400">PDF, Images (max 10MB)</p>
              </div>
              <input type="file" className="hidden" multiple accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.ai,.heic,.heif" onChange={handleFileUpload} disabled={isUploading} />
            </label>
          </div>
          {attachments.length === 0 ? <p className="text-center text-zinc-400 py-4">Aucune pièce jointe</p> : (
            <div className="space-y-2">
              {attachments.map((att) => {
                const isPreviewable = ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp'].includes(att.file_type?.toLowerCase());
                return (
                  <div key={att.attachment_id} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg hover:bg-zinc-100 transition-colors">
                    <div className={`flex items-center gap-3 flex-1 ${isPreviewable ? 'cursor-pointer' : ''}`} onClick={() => isPreviewable && previewAttachment(attachmentModal.orderId, att)}>
                      {att.file_type === '.pdf' ? <FileText className="w-5 h-5 text-red-500" /> : ['.png', '.jpg', '.jpeg', '.gif', '.webp'].includes(att.file_type) ? <Image className="w-5 h-5 text-green-500" /> : <File className="w-5 h-5 text-zinc-500" />}
                      <div>
                        <p className={`text-sm font-medium ${isPreviewable ? 'text-blue-600 hover:underline' : 'text-zinc-700'}`}>{att.original_filename}</p>
                        <p className="text-xs text-zinc-400">{(att.file_size / 1024).toFixed(1)} KB • {new Date(att.uploaded_at).toLocaleDateString('fr-FR')}{att.print_with_sheet ? <span className="ml-2 text-orange-600 font-medium" data-testid={`attachment-print-badge-${att.attachment_id}`}>Imprimée avec la fiche{att.fabric_label ? ` · ${att.fabric_label}` : ''}</span> : null}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {['.png', '.jpg', '.jpeg', '.gif', '.webp'].includes(att.file_type?.toLowerCase()) && handleTogglePrintWithSheet && (
                        <button onClick={() => handleTogglePrintWithSheet(att)} className={`p-1 rounded transition-colors ${att.print_with_sheet ? 'bg-orange-100 hover:bg-orange-200' : 'hover:bg-orange-50'}`} title={att.print_with_sheet ? "Ne plus imprimer avec la fiche" : "Imprimer avec la fiche"} data-testid={`attachment-print-toggle-${att.attachment_id}`}>
                          <Printer className={`w-4 h-4 ${att.print_with_sheet ? 'text-orange-600' : 'text-zinc-400'}`} />
                        </button>
                      )}
                      {isPreviewable && <button onClick={() => previewAttachment(attachmentModal.orderId, att)} className="p-1 hover:bg-blue-100 rounded" title="Aperçu"><Eye className="w-4 h-4 text-blue-600" /></button>}
                      <button onClick={() => downloadAttachment(attachmentModal.orderId, att.attachment_id)} className="p-1 hover:bg-zinc-200 rounded" title="Télécharger"><Download className="w-4 h-4 text-zinc-600" /></button>
                      <button onClick={() => handleDeleteAttachment(att.attachment_id)} className="p-1 hover:bg-red-100 rounded" title="Supprimer"><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function EmailsModal({
  emailModal, closeEmailModal, emailForm, handleEmailContentChange,
  detectedAttachments, emailAttachments, removeEmailAttachment,
  handleEmailAttachmentSelect, handleAddEmail, isUploading, emails,
}) {
  const [highlightedType, setHighlightedType] = React.useState(null);
  const scrollToEmail = (type) => {
    const el = document.querySelector(`[data-email-type="${type}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedType(type);
      setTimeout(() => setHighlightedType(null), 2000);
    }
  };
  if (!emailModal.open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-3"><MessageSquare className="w-5 h-5 text-blue-600" /><h3 className="text-lg font-semibold text-zinc-900">Emails - {emailModal.orderName}</h3></div>
          <button onClick={closeEmailModal} className="p-1 hover:bg-zinc-200 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto max-h-[60vh]">
          <div className="mb-4 p-4 bg-zinc-50 rounded-lg space-y-3">
            <h4 className="text-sm font-medium text-zinc-700">Ajouter un email</h4>
            <textarea placeholder="Collez le contenu de l'email ici..." value={emailForm.content} onChange={(e) => handleEmailContentChange(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm h-32 resize-none" />
            {detectedAttachments.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Paperclip className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Pièces jointes détectées dans l'email</p>
                    <p className="text-xs text-amber-600 mt-1">Mentions trouvées : {detectedAttachments.join(', ')}</p>
                    <p className="text-xs text-amber-700 mt-2">N'oubliez pas d'ajouter les fichiers ci-dessous !</p>
                  </div>
                </div>
              </div>
            )}
            <div className="border-t border-zinc-200 pt-3">
              <label className="text-sm font-medium text-zinc-700 flex items-center gap-2 mb-2"><Upload className="w-4 h-4" />Pièces jointes de l'email</label>
              {emailAttachments.length > 0 && (
                <div className="mb-2 space-y-1">
                  {emailAttachments.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between px-3 py-2 bg-white border border-zinc-200 rounded-lg">
                      <div className="flex items-center gap-2"><File className="w-4 h-4 text-zinc-400" /><span className="text-sm text-zinc-700 truncate max-w-[250px]">{file.name}</span><span className="text-xs text-zinc-400">({(file.size / 1024).toFixed(1)} KB)</span></div>
                      <button onClick={() => removeEmailAttachment(idx)} className="p-1 hover:bg-red-100 rounded"><XIcon className="w-4 h-4 text-red-500" /></button>
                    </div>
                  ))}
                </div>
              )}
              <label className="flex items-center justify-center w-full h-16 border-2 border-dashed border-zinc-300 rounded-lg cursor-pointer hover:bg-zinc-100 transition-colors">
                <div className="flex items-center gap-2 text-zinc-500"><Plus className="w-5 h-5" /><span className="text-sm">Ajouter des fichiers</span></div>
                <input type="file" className="hidden" multiple accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.ai" onChange={handleEmailAttachmentSelect} />
              </label>
            </div>
            <Button onClick={handleAddEmail} className="bg-blue-500 hover:bg-blue-600 text-white" size="sm" disabled={isUploading}>
              {isUploading ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />Envoi en cours...</> : <><Plus className="w-4 h-4 mr-1" />Ajouter {emailAttachments.length > 0 ? `(+ ${emailAttachments.length} PJ)` : ''}</>}
            </Button>
          </div>
          {emails.length === 0 ? <p className="text-center text-zinc-400 py-4">Aucun email enregistré</p> : (
            <div className="space-y-4">
              <div className="flex gap-2 border-b border-zinc-200 pb-2">
                {emails.some(e => e.type === 'commande') && <button data-testid="goto-commande-email-btn" onClick={() => scrollToEmail('commande')} className="px-3 py-1 bg-orange-100 text-orange-700 hover:bg-orange-200 text-sm rounded-full flex items-center gap-1 cursor-pointer transition-colors" title="Aller à l'email de commande"><Package className="w-3 h-3" />Commande</button>}
                {emails.some(e => e.type === 'reglement') && <button data-testid="goto-reglement-email-btn" onClick={() => scrollToEmail('reglement')} className="px-3 py-1 bg-green-100 text-green-700 hover:bg-green-200 text-sm rounded-full flex items-center gap-1 cursor-pointer transition-colors" title="Aller à l'email de règlement"><CreditCard className="w-3 h-3" />Règlement</button>}
              </div>
              {emails.map((email, idx) => (
                <div key={idx} data-email-type={email.type} className={`p-4 border rounded-lg transition-all duration-300 ${email.type === 'commande' ? 'bg-orange-50 border-orange-200' : email.type === 'reglement' ? 'bg-green-50 border-green-200' : 'bg-white border-zinc-200'} ${highlightedType === email.type ? 'ring-2 ring-offset-2 ' + (email.type === 'commande' ? 'ring-orange-400' : 'ring-green-400') : ''}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {email.type === 'commande' ? <Package className="w-4 h-4 text-orange-600" /> : email.type === 'reglement' ? <CreditCard className="w-4 h-4 text-green-600" /> : <Mail className="w-4 h-4 text-blue-600" />}
                      <span className="font-medium text-sm">{email.type === 'commande' ? 'Email de commande' : email.type === 'reglement' ? 'Email de règlement' : email.subject || 'Email'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {email.date && <span className="text-xs text-zinc-500">{new Date(email.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
                      <button onClick={() => { navigator.clipboard.writeText(email.content); toast.success('Email copié !'); }} className="p-1 hover:bg-zinc-200 rounded" title="Copier"><Copy className="w-4 h-4 text-zinc-500" /></button>
                    </div>
                  </div>
                  <div className="bg-white/50 rounded p-3 max-h-64 overflow-y-auto"><p className="text-sm text-zinc-700 whitespace-pre-wrap font-mono">{email.content}</p></div>
                  {email.added_by && <p className="text-xs text-zinc-400 mt-2">Ajouté par {email.added_by}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AddColumnModal({
  showAddColumnModal, setShowAddColumnModal, newColumnForm, setNewColumnForm,
  COLUMN_TYPES_WITH_ICONS, handleCreateColumn, isCreatingColumn,
}) {
  if (!showAddColumnModal) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setShowAddColumnModal(false)}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-zinc-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Plus className="w-5 h-5 text-orange-500" /><h3 className="text-lg font-semibold text-zinc-900">Nouvelle colonne</h3></div>
            <button onClick={() => setShowAddColumnModal(false)} className="text-zinc-400 hover:text-zinc-600"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Nom de la colonne</label>
            <Input type="text" value={newColumnForm.name} onChange={(e) => setNewColumnForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Ex: MARGE NETTE" className="w-full" autoFocus onKeyDown={(e) => e.key === 'Enter' && handleCreateColumn()} data-testid="new-column-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">Type de colonne</label>
            <div className="grid grid-cols-2 gap-2">
              {COLUMN_TYPES_WITH_ICONS.map((type) => { const Icon = type.icon; return (
                <button key={type.value} type="button" onClick={() => setNewColumnForm(prev => ({ ...prev, type: type.value }))} className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${newColumnForm.type === type.value ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-zinc-200 hover:border-zinc-300'}`}>
                  <Icon className="w-4 h-4" /><span className="text-sm">{type.label}</span>
                </button>
              ); })}
            </div>
          </div>
          <p className="text-xs text-zinc-500">Vous pourrez configurer les options avancées après la création.</p>
        </div>
        <div className="p-6 border-t border-zinc-200 flex justify-end gap-3">
          <Button variant="outline" onClick={() => setShowAddColumnModal(false)}>Annuler</Button>
          <Button onClick={handleCreateColumn} disabled={isCreatingColumn || !newColumnForm.name.trim()} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="confirm-create-column-btn">
            {isCreatingColumn ? <><Loader2 className="w-4 h-4 animate-spin" />Création...</> : <><Plus className="w-4 h-4" />Créer la colonne</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function StatusFilterDropdown({
  showStatusFilter, setShowStatusFilter, statusFilterPosition,
  availableStatuses, statusFilter, toggleStatusFilter,
  selectAllStatuses, clearStatusFilter,
  label = "Filtrer par statut", dropdownClass = "status-filter-dropdown",
}) {
  if (!showStatusFilter) return null;
  return (
    <div className={`${dropdownClass} fixed w-72 bg-white rounded-lg shadow-xl border border-zinc-200 text-zinc-900`} style={{ top: `${statusFilterPosition.top}px`, right: `${statusFilterPosition.right}px`, zIndex: 9999 }} onClick={(e) => e.stopPropagation()}>
      <div className="p-3 border-b border-zinc-100">
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium text-sm">{label}</span>
          <button onClick={() => setShowStatusFilter(false)} className="p-1 hover:bg-zinc-100 rounded"><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-2">
          <button onClick={selectAllStatuses} className="text-xs px-2 py-1 bg-zinc-100 hover:bg-zinc-200 rounded">Tout sélectionner</button>
          <button onClick={clearStatusFilter} className="text-xs px-2 py-1 bg-zinc-100 hover:bg-zinc-200 rounded">Tout désélectionner</button>
        </div>
      </div>
      <div className="max-h-64 overflow-y-auto p-2">
        {availableStatuses.length === 0 ? <p className="text-sm text-zinc-500 italic p-2">Aucun statut disponible</p> : (
          availableStatuses.map((status) => <label key={status} className="flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 rounded cursor-pointer"><Checkbox checked={statusFilter.includes(status)} onCheckedChange={() => toggleStatusFilter(status)} /><span className="text-sm">{status}</span></label>)
        )}
      </div>
      {statusFilter.length > 0 && <div className="p-2 border-t border-zinc-100 bg-zinc-50 rounded-b-lg"><p className="text-xs text-zinc-600">{statusFilter.length} statut{statusFilter.length > 1 ? 's' : ''} sélectionné{statusFilter.length > 1 ? 's' : ''}</p></div>}
    </div>
  );
}

export function PreviewLightbox({ previewModal, closePreviewModal }) {
  if (!previewModal.open) return null;
  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60]" onClick={closePreviewModal}>
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4 bg-gradient-to-b from-black/60 to-transparent">
        <span className="text-white font-medium">{previewModal.filename}</span>
        <div className="flex items-center gap-2">
          <a href={previewModal.url?.replace('/preview', '/download')} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors" onClick={(e) => e.stopPropagation()} title="Télécharger"><Download className="w-5 h-5 text-white" /></a>
          <button onClick={closePreviewModal} className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors" title="Fermer"><X className="w-5 h-5 text-white" /></button>
        </div>
      </div>
      <div className="max-w-[90vw] max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        {previewModal.type === 'image' ? <img src={previewModal.url} alt={previewModal.filename} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" /> : previewModal.type === 'pdf' ? <iframe src={previewModal.url} title={previewModal.filename} className="w-[80vw] h-[85vh] rounded-lg shadow-2xl bg-white" /> : null}
      </div>
    </div>
  );
}
